import { Button } from "@/components/ui/button";
import { Check, Sparkles, X } from "lucide-react";

export default function Pricing() {
  const plans = [
    {
      name: "Básico",
      description: "Ideal para empezar a organizar tu negocio",
      price: "$2,500",
      features: [
        { name: "Tiendas máximas", value: "1" },
        { name: "Productos máximos", value: "15" },
        {
          name: "Punto de Venta",
          value:
            "Básico (Sin apertura/cierre de turno, caja chica, caja ciega)",
        },
        { name: "Inventario", value: "Básico (Precio, stock, umbral)" },
        { name: "Usuarios", value: "2 (1 admin + 1 cajero)" },
        { name: "Acceso a clientes", value: "No disponible" },
        { name: "Acceso a reportes", value: "No disponible" },
        { name: "Notificaciones Telegram", value: "No disponible" },
        { name: "Soporte", value: "No disponible" },
      ],
      color:
        "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900",
      buttonVariant: "outline" as const,
    },
    {
      name: "Profesional",
      description: "La solución completa para tu crecimiento",
      price: "$5000",
      popular: true,
      features: [
        { name: "Tiendas máximas", value: "3" },
        { name: "Productos máximos", value: "50" },
        {
          name: "Punto de Venta",
          value: "Completo (Apertura/cierre, caja chica, caja ciega)",
        },
        { name: "Inventario", value: "Avanzado completo" },
        { name: "Usuarios", value: "10 por tienda (admin, manager, cashier)" },
        {
          name: "Clientes",
          value: "Módulo completo + historial + estadísticas",
        },
        {
          name: "Reportes",
          value: "Ventas, productos, clientes, discrepancias",
        },
        {
          name: "Notificaciones Telegram",
          value: "Bot básico (alertas y resúmenes diarios)",
        },
        { name: "Soporte", value: "WhatsApp/Telegram dedicado" },
      ],
      color:
        "border-primary bg-gradient-to-b from-primary/5 to-transparent shadow-xl shadow-primary/10 dark:bg-slate-800 dark:from-slate-800",
      buttonVariant: "default" as const,
    },
    {
      name: "Enterprise",
      description: "Para negocios sin límites",
      price: "$8000",
      features: [
        { name: "Tiendas", value: "Ilimitadas" },
        { name: "Productos", value: "Ilimitados" },
        { name: "Punto de Venta", value: "Completo" },
        { name: "Inventario", value: "Avanzado completo" },
        { name: "Usuarios", value: "Ilimitados" },
        {
          name: "Clientes",
          value: "Módulo completo + historial + estadísticas",
        },
        {
          name: "Reportes",
          value: "Consolidados multi-tienda, heatmaps horarios",
        },
        { name: "Notificaciones Telegram", value: "Bot básico" },
        {
          name: "Soporte",
          value: "Dedicado, 2h respuesta, onboarding personalizado",
        },
      ],
      color:
        "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900",
      buttonVariant: "outline" as const,
    },
  ];

  const isNotAvailable = (value: string) => {
    return (
      value &&
      (value.toLowerCase().includes("no disponible") ||
        value.toLowerCase().includes("básico"))
    );
  };

  return (
    <section
      id="planes"
      className="py-16 sm:py-20 lg:py-24 bg-slate-50 dark:bg-slate-900/50"
    >
      <div className="container px-4 mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-bold mb-4 sm:mb-6">
            <Sparkles className="w-4 h-4" />
            <span>Planes a tu medida</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 tracking-tight leading-tight font-display text-slate-900 dark:text-white">
            Escala tu negocio con QvaPOS
          </h2>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed px-4">
            Elige el plan que mejor se adapte a tus necesidades. Sin costos
            ocultos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-2xl sm:rounded-3xl lg:rounded-[2rem] p-5 sm:p-6 lg:p-8 border-2 ${plan.color} flex flex-col h-full transition-all hover:-translate-y-1`}
            >
              {plan.popular && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-3 sm:px-4 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider shadow-lg whitespace-nowrap">
                  Más Popular
                </div>
              )}

              <div className="mb-5 sm:mb-6 lg:mb-8">
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mb-1 sm:mb-2">
                  {plan.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">
                  {plan.description}
                </p>
              </div>

              <div className="mb-5 sm:mb-6 lg:mb-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white">
                    {plan.price}
                  </span>
                  <span className="text-slate-500 text-xs sm:text-sm">
                    CUP/mes
                  </span>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 flex-grow mb-6 sm:mb-8">
                {plan.features.map((feature, fIndex) => {
                  const unavailable = isNotAvailable(feature.value);
                  return (
                    <div
                      key={fIndex}
                      className="flex items-start gap-2 sm:gap-3"
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {unavailable ? (
                          <X className="w-3 h-3 sm:w-4 sm:h-4 text-slate-300 dark:text-slate-600" />
                        ) : (
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 text-secondary" />
                        )}
                      </div>
                      <div>
                        <span
                          className={`text-xs sm:text-sm font-semibold ${unavailable ? "text-slate-400 dark:text-slate-500 line-through" : "text-slate-700 dark:text-slate-300"}`}
                        >
                          {feature.name}
                        </span>
                        {feature.value && (
                          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">
                            {feature.value}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button
                onClick={() =>
                  (window.location.href = "http://127.0.0.1:5000/register")
                }
                variant={plan.buttonVariant}
                className={`w-full h-11 sm:h-12 lg:h-14 rounded-full text-sm sm:text-base font-bold ${
                  plan.popular
                    ? "bg-primary hover:bg-primary/90 text-white shadow-xl shadow-primary/20"
                    : "border-2 border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-primary/5"
                }`}
              >
                Elegir {plan.name}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
